/**
 * UI Key Registry — Single source of truth for every filter/field key used in the application.
 *
 * RULES:
 *  1. All values are camelCase — no underscores, no short codes, no PascalCase.
 *  2. Every key the UI references (components, hooks, state, config) must come from here.
 *  3. The apiAdapter.ts is the ONLY other file allowed to know what the API actually sends.
 *  4. To rename a UI key: change it here. To handle an API rename: change apiAdapter.ts only.
 */

// ─────────────────────────────────────────────────────────────────────────────
// Filter Keys — used in filter state, filter components, data.json config
// ─────────────────────────────────────────────────────────────────────────────

export const FILTER_KEYS = {
  // ── Common (EAME + shared) ──────────────────────────────────────────────────
  YEAR: 'year',
  COUNTRY: 'country',
  STATE: 'state',
  TRIAL_TYPE: 'trialType',
  TRIAL_ID: 'trialId',
  TRIAL_SOURCE: 'trialSource',
  TRIAL_TREATMENT: 'trialTreatment',
  TEMP_PRECIP: 'tempPrecip',
  TEMP_PRECIP_RAW: 'tempPrecipRaw',
  HARVEST_DAY: 'harvestDay',
  HARVEST_MONTH: 'harvestMonth',
  PLANTING_DATE: 'plantingDate',
  PLANTING_DAY: 'plantingDay',
  PLANTING_MONTH: 'plantingMonth',
  TEXTURE: 'texture',
  TEXTURE_HU: 'textureHu',
  TEXTURE_RAW: 'textureRaw',
  DRAINAGE: 'drainage',
  EXPERIMENTS: 'experiments',
  CEC: 'cec',
  PH: 'ph',
  ORTHOGONAL_MODE: 'orthogonalMode',

  // ── EAME filters (v2 API uses descriptive names; old short codes: chks, awc, om, prec, rad, temp, humi) ──
  ENTRIES: 'entries',
  WATER_HOLDING_CAPACITY: 'waterHoldingCapacity', // API: water_holding_capacity
  ORGANIC_MATTER: 'organicMatter', // API: organic_matter
  PRECIPITATION: 'precipitation',
  RADIATION: 'radiation',
  TEMPERATURE: 'temperature',
  HUMIDITY: 'humidity',
  GENERIC_YIELD: 'genericYield', // API: generic_yield
  RELATIVE_MATURITY: 'relativeMaturity', // API short code: rm
  ALTERNATE_TRAIT: 'alternateTrait', // API: alternate_trait (global Performance Parameter filter)
  WITHIN_TRIAL: 'withinTrial', // API: WithInTrial
  TILLAGE: 'cultivation',
  SCUBE: 'scube',
  SCUBE_PH: 'scubePh', // API: scube_ph
  WCUBE: 'wcube',
  PREVIOUS_CROP: 'previousCrop', // API: previous_crop
  TPP: 'tpp',

  // ── EAME check-type filters ─────────────────────────────────────────────────
  CHECK_STAGE: 'stage', // API v2: stage (legacy: chekstg)
  CHECK_MATURITY_GROUP: 'checkMg', // API v2: maturity_group (legacy: chkmg)
  CHECK_HERBICIDE: 'checkHerbicide', // API v2: herbicide_technology (legacy: chkherbicide)
  CHECK_OIL_CONTENT: 'checkOilContent', // API v2: oil_content (legacy: chkoilcontent)
  CHECK_FAO: 'checkFao', // API v2: relative_maturity_fao (legacy: chekrm, still decoded)
  CHECK_COMPANY: 'checkCmp', // API: chkcmp

  // ── EAME filter config ──────────────────────────────────────────────────────
  MIN_LOCATIONS: 'minLocations', // API: min_no_of_locations
  TPR_FILTER_TYPE: 'tprFilterType', // API: tpr_filter_type
  IS_EXPERIMENTAL: 'isExperimental', // API: isexpermental

  // ── LATAM-specific ──────────────────────────────────────────────────────────
  CROP_SEASON: 'cropSeason', // API: crop_season
  PLANTING_WINDOW: 'plantingWindow', // API: planting_window
  EXPERIMENT_STAGE: 'expStage', // API: exp_stage
  AREA_TYPE: 'areaType', // API: areatype_c
  ENV_CLASS: 'envClass', // API: env_class
  // BR/LATAM weather filter. The classification itself already reaches the app on the
  // Timeline (see feature/tabs/timeline/climateDimension.ts); this is the same ENSO phase
  // offered as a filter facet. Response spellings vary - see WIRE_TO_UI in apiAdapter.
  ENSO: 'enso', // API: enso
  MESOREGION_CODE: 'mesoregionCode', // API: mesoregion_code
  MACROREGION_CODE: 'macroregionCode', // API: macroregion_code
  STATE_CODE: 'stateCode', // API: state_code
  REC: 'rec',
  RTP: 'rtp',
  PLANTING_DENSITY: 'plantingDensity', // API request: plntd; response: planting_density
  GROWING_DEGREE_DAYS: 'gdd', // API: growing_degree_days

  // ── LATAM range filters ─────────────────────────────────────────────────────
  AVG_YIELD: 'avgYield', // API: avg_ygsmn
  AVG_HARVEST_POPULATION: 'avgHarvestPopulation', // API v2: harvest_population (range; legacy v1 avg_havpn still decoded)
  AVG_OIL_PERCENT: 'avgOilPct', // API: avg_oil_p
  OIL_PERCENT: 'oilPct', // API: oil_p
  AVG_GRAIN_MOISTURE: 'avgGrainMoisture', // API: avg_gmstp
  GRAIN_MOISTURE: 'grainMoisture', // API: gmstp
  YIELD: 'yield', // API: ygsmn
  RELATIVE_MATURITY_N: 'rmtN', // API: rmt_n

  // ── Date-number range filters (LATAM) ──────────────────────────────────────
  PLANTING_DATE_NUMBER: 'plantingDateNumber', // API: planting_date
  HARVEST_DATE_NUMBER: 'harvestDateNumber', // API: harvest_date

  // ── TPR type (hidden in FilterHeader when no country) ───────────────────────
  TPR_TYPE: 'tprType' // API: tpr_type
} as const;

export type FilterKey = (typeof FILTER_KEYS)[keyof typeof FILTER_KEYS];

// ─────────────────────────────────────────────────────────────────────────────
// Wire key/field names — the API's snake_case keys used in request/response
// bodies. Centralized here (single source) so the same string is never
// duplicated across encoders/features (SonarLint S1192) and any wire rename
// happens in one place. Both the adapter (services) and UI import from here.
// ─────────────────────────────────────────────────────────────────────────────

export const WIRE = {
  ENTRIES: 'entries',
  PRODUCT: 'product',
  GENERIC_YIELD: 'generic_yield',
  YIELD_LVL: 'yield_lvl',
  MIN_LOCATIONS: 'min_no_of_locations',
  CROP_SEASON: 'crop_season',
  TPR_FILTER_TYPE: 'tpr_filter_type',
  RMT_N: 'rmt_n',
  // Range-input wire keys (split into _from/_to by the encoders)
  YGSMN: 'ygsmn',
  AVG_YGSMN: 'avg_ygsmn',
  GMSTP: 'gmstp',
  AVG_GMSTP: 'avg_gmstp',
  PLANTING_DENSITY: 'planting_density',
  HARVEST_POPULATION: 'harvest_population',
  PLANTING_DATE: 'planting_date',
  HARVEST_DATE: 'harvest_date',
  GROWING_DEGREE_DAYS: 'growing_degree_days',
  RM: 'rm',
  TEMP_PRECIP_RAW: 'temp_precip_raw',
  TEMP_PRECIP_RATIO_RANGE: 'temperature_precipitation_ratio_range',
  ORTHOGONAL_MODE_ENABLED: 'ORTHOGONAL_MODE_ENABLED',
  // Heatmap/scatter trait-axis params
  X_TRAIT: 'x_trait',
  Y_TRAIT: 'y_trait',
  TRAIT: 'trait',
  TRAITS: 'traits',
  // LATAM main-filter response short codes
  YL: 'yl',
  CHKS: 'chks'
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// Response Field Keys — used when reading API response objects in the UI
// ─────────────────────────────────────────────────────────────────────────────

export const RESPONSE_KEYS = {
  ENTRY: 'entry',
  ENTRY_TYPE: 'entryType',
  ENTRY_DISPLAY_NAME: 'entryDisplayName',
  COMPANY: 'company',
  CROP: 'crop',
  REGION: 'region',
  YEAR: 'year',
  RAW_DATA: 'rawData',
  STATS: 'stats',
  DATA: 'data'
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// Request Field Keys — used when building objects passed to service functions
// ─────────────────────────────────────────────────────────────────────────────

export const REQUEST_FIELDS = {
  CROP: 'crop',
  REGION: 'region',
  ENTRY: 'entry',
  ENTRY_TYPE: 'entryType',
  COUNTRY: 'country',
  COUNTRY_ZONE: 'countryZone',
  UNIT_TYPE: 'userYieldUnit',
  IS_ORTHOGONAL: 'isOrthogonalMode',
  THRESHOLD_TYPE: 'thresholdType',
  IS_EXPORT: 'isExportRawData',
  BUSY_LOADING: 'busyLoading',
  PERSONA: 'persona'
} as const;
